import classNames from "./SidePanel.module.css";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useLocalParticipant,
  useRoomContext,
} from "@livekit/components-react";
import { createRef, useCallback, useEffect, useState } from "react";
import useUser, { useRenderLanguage, useUserMetadata } from "../../hooks/user";
import UserLanguageSelect from "../common/UserLanguageSelect";
import Voice from "../common/Voice";
import Captions from "../common/Captions";
import { usePublisherToken } from "../../hooks/token";
import * as teams from "@microsoft/teams-js";
import {
  useInMeeting,
  useIsInTeamsMobile,
  useTeamsContext,
  useTeamsMeetingType,
} from "../../hooks/teams";
import Loader from "../common/Loader";
import { getLanguageFlag, getLanguageName, getTextLanguage } from "../../types";
import { useAxios } from "../../hooks/axios";
import api, { BotStatus, Organization, Room } from "../../hooks/api";
import useCaptions from "../../hooks/captions";
import { IoEllipsisHorizontal } from "react-icons/io5";
import { useDominantSpeakerLanguage } from "../../hooks/speaker";
import { usePollRoomMinutesRemaining, useRoom } from "../context/room/room";
import CreateOrEditRoom from "../routes/room/CreateOrEditRoom";
import { OrganizationProvider } from "../context/organization/OrganizationProvider";
import { useDefaultSettings } from "../../hooks/settings";
import { useCurrentPlan } from "../context/organization/organization";
import { useGetStoredSettings } from "../../hooks/layout";
import Button from "../common/Button";
import Header from "../Header";
import { MdOutlineClosedCaption } from "react-icons/md";
import UpgradeNow from "../common/UpgradeNow";

export const Translator = (props: {
  room: Room;
  organization: Organization;
  participantType?: "speaker" | "viewer";
  participantName?: string;
}) => {
  useUserMetadata();
  useDefaultSettings();
  useGetStoredSettings();
  usePollRoomMinutesRemaining();
  const teamsContext = useTeamsContext();
  const isInTeamsMobile = useIsInTeamsMobile();

  const [captions] = useCaptions({ teamsContext: teamsContext });
  const [user, setUser] = useUser();
  const { localParticipant } = useLocalParticipant();

  const pipPadding = 20;
  const pipFontGap = 10;
  const totalLinesToRender = 4;
  const pipWidth = 600;
  const pipFontSize = 24;
  const pipHeight =
    pipPadding * 2 +
    pipFontSize * totalLinesToRender +
    pipFontGap * (totalLinesToRender - 1);

  const [overlayActive, setOverlayActive] = useState(false);

  // Render captions to the canvas for pip mode
  const renderLanguage = useRenderLanguage();
  const canvas = createRef<HTMLCanvasElement>();
  useEffect(() => {
    let animationFrameId: number | undefined = undefined;
    const render = async () => {
      if (!canvas.current) {
        return;
      }

      const scale = window.devicePixelRatio;
      canvas.current.width = Math.floor(pipWidth * scale);
      canvas.current.height = Math.floor(pipHeight * scale);

      const context = canvas.current.getContext("2d");
      if (!context) {
        return;
      }
      // Normalize coordinate system to use CSS pixels.
      context.scale(scale, scale);

      context.fillStyle = "#101010";
      context.fillRect(0, 0, canvas.current.width, canvas.current.height);

      // Determine which captions to render
      const lastCaptions = Object.values(captions)
        // Filter captions for local participant
        .filter((caption) => caption.identity !== localParticipant.identity)
        .slice(-totalLinesToRender);

      const textLanguage =
        (renderLanguage && getTextLanguage(renderLanguage)) ||
        props.room.languages[0];
      if (!textLanguage) {
        return;
      }

      const captionsToRender = lastCaptions
        .filter((caption) => !!caption.translations[textLanguage])
        .map(
          (caption) =>
            `${caption.name.split(" ")[0] || caption.name}:\t${caption.translations[textLanguage]
            }`,
        );

      context.fillStyle = "white";
      context.font = `${pipFontSize}px sans-serif`;
      context.textAlign = "left";
      context.textBaseline = "bottom";
      context.textRendering = "optimizeLegibility";

      captionsToRender
        .reduce((result: string[], text: string) => {
          let currentLine = "";
          const words = text.split(" ");
          for (const word of words) {
            // If entire word takes line, split by char
            const measureWord = context.measureText(word);
            if (measureWord.width > pipWidth - pipPadding * 2) {
              for (const char of word) {
                const measure = context.measureText(currentLine + char);
                if (measure.width > pipWidth - pipPadding * 2) {
                  result.push(currentLine);
                  currentLine = char;
                } else {
                  currentLine += char;
                }
              }
            }
            // Split by empty space
            else {
              const measure = context.measureText(currentLine + word);
              if (measure.width > pipWidth - pipPadding * 2) {
                result.push(currentLine.trim());
                currentLine = word + " ";
              } else {
                currentLine += word + " ";
              }
            }
          }

          if (currentLine.length > 0) {
            result.push(currentLine.trim());
          }

          return result;
        }, [])
        .slice(-totalLinesToRender)
        .forEach((line, index) => {
          context.fillStyle = "white";
          context.fillText(
            line,
            pipPadding,
            pipPadding + (index + 1) * pipFontSize + index * pipFontGap,
          );
          // Debug lines
          // context.fillStyle = "red";
          // context.fillRect(pipPadding, 0, 1, pipHeight);
          // context.fillRect(pipWidth - pipPadding, 0, 1, pipHeight);
          // context.fillRect(
          //   0,
          //   pipPadding + index * pipFontSize + index * pipFontGap,
          //   canvas.current?.width || 0,
          //   1
          // );
          // context.fillRect(
          //   0,
          //   (index + 1) * pipFontSize + pipPadding + index * pipFontGap,
          //   canvas.current?.width || 0,
          //   1
          // );
        });

      animationFrameId = requestAnimationFrame(render);
    };
    render();
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [
    canvas,
    captions,
    renderLanguage,
    pipFontSize,
    pipPadding,
    pipWidth,
    pipHeight,
    localParticipant.identity,
    props.room.languages,
  ]);

  const toggleOverlay = useCallback(() => {
    if (!canvas.current) {
      return;
    }
    const stream = canvas.current.captureStream();
    const videoElement = document.createElement("video");
    videoElement.hidden = true;
    videoElement.muted = true;
    videoElement.autoplay = true;
    videoElement.srcObject = stream;

    const onRemove = () => {
      stream.getTracks().forEach((t) => t.stop());
      videoElement.remove();
      setOverlayActive(false);
    };

    videoElement.onloadedmetadata = async () => {
      videoElement.onleavepictureinpicture = () => {
        onRemove();
      };
      try {
        await videoElement.requestPictureInPicture();
        setOverlayActive(true);
      } catch {
        onRemove();
      }
    };

    videoElement.play();
  }, [canvas]);

  const inMeeting = useInMeeting();

  // Default voice on when set in room config
  useEffect(() => {
    if (props.room?.default_voice_on) {
      setUser((prevState) => ({
        ...prevState,
        metadata: {
          ...prevState.metadata,
          // Don't turn on with auto detect selected
          voice:
            prevState.metadata.selectedLanguage !== "autodetect"
              ? "ai"
              : undefined,
        },
      }));
    }
  }, [props.room?.default_voice_on, setUser]);

  useEffect(() => {
    setUser((prevState) => ({
      ...prevState,
      metadata: {
        ...prevState.metadata,
        // Check if selected language is in room, if not set selected language to english
        selectedLanguage: props.room.languages.includes(
          prevState.metadata.selectedLanguage,
        )
          ? prevState.metadata.selectedLanguage
          : props.room.languages.includes("en-US")
            ? "en-US"
            : props.room.languages[0],
        participantType: props.participantType ?? "viewer",
        name: props.participantName,
        botParticipantId: teamsContext?.user?.id,
      },
    }));
  }, [setUser, teamsContext?.user?.id, props.room.languages, props.participantType, props.participantName]);

  const meetingType = useTeamsMeetingType();
  const currentPlan = useCurrentPlan(props.organization);
  const isUpgradeNowVisible = UpgradeNow.useIsUpgradeNowVisible(props.organization, user.auth, currentPlan);

  const dominantSpeakerLanguage = useDominantSpeakerLanguage();

  useEffect(() => {
    if (!inMeeting) {
      return;
    }
    teams.meeting.getIncomingClientAudioState((err, alreadyMuted) => {
      if (err) {
        console.error(err);
        return;
      }
      if (
        // Toggle meeting audio on when muted and ai voice turned off
        (alreadyMuted &&
          (!user.metadata.voice ||
            !dominantSpeakerLanguage ||
            dominantSpeakerLanguage === renderLanguage)) ||
        // Toggle meeting audio off when not muted, ai voice turned on, and dominant speaker speaking different language
        (!alreadyMuted &&
          !!user.metadata.voice &&
          !!dominantSpeakerLanguage &&
          dominantSpeakerLanguage !== renderLanguage)
      ) {
        console.debug(
          "toggle teams audio...",
          dominantSpeakerLanguage,
          renderLanguage,
        );
        teams.meeting.toggleIncomingClientAudio((err) => {
          if (err) {
            console.error(err);
            return;
          }
        });
      }
    });
  }, [
    inMeeting,
    user.metadata.voice,
    dominantSpeakerLanguage,
    user.metadata.selectedLanguage,
    renderLanguage,
  ]);

  // Best-effort match this sidepanel viewer to their own Recall-published
  // speaker stream, by name/email, so they can retarget that stream's source
  // language.
  const room = useRoomContext();
  const [correlatedSpeaker, setCorrelatedSpeaker] = useState<
    { identity: string; sid: string; recallParticipantId: number } | undefined
  >();

  useEffect(() => {
    const normalize = (value?: string) => (value ? value.trim().toLowerCase() : undefined);
    // Primary match: the Microsoft Entra object id. teamsContext.user.id is
    // that id directly. The bridge extracts the same id from Recall's Teams
    // participant data (extra_data.microsoft_teams.user_id is an MRI shaped
    // "8:orgid:<AAD object id>" for tenant members - see
    // livekit-publisher.ts's extractTeamsAadId) and publishes it as
    // teams_aad_id. Name/email are a fallback for participants Recall
    // couldn't resolve an id for (e.g. guests) - Recall never exposes an
    // attendee's email for Teams at all, so email realistically never
    // matches there, but costs nothing to keep as a fallback.
    const myAadId = normalize(teamsContext?.user?.id);
    const myName = normalize(teamsContext?.user?.displayName);
    const myEmail =
      normalize(teamsContext?.user?.userPrincipalName) ||
      normalize(teamsContext?.user?.loginHint);

    const recompute = () => {
      if (!myAadId && !myName && !myEmail) {
        setCorrelatedSpeaker(undefined);
        return;
      }

      const matches = Array.from(room.remoteParticipants.values()).flatMap(
        (participant) => {
          if (!participant.metadata) {
            return [];
          }
          try {
            const metadata = JSON.parse(participant.metadata) as {
              participant_type?: string;
              source?: string;
              name?: string;
              email?: string;
              teams_aad_id?: string;
              recall_participant_id?: number;
            };
            if (
              metadata.participant_type !== "speaker" ||
              metadata.source !== "recall" ||
              metadata.recall_participant_id === undefined
            ) {
              return [];
            }
            const isMatch =
              (!!myAadId && normalize(metadata.teams_aad_id) === myAadId) ||
              (!!myEmail && normalize(metadata.email) === myEmail) ||
              (!!myName && normalize(metadata.name) === myName);
            return isMatch
              ? [
                {
                  identity: participant.identity,
                  sid: participant.sid,
                  recallParticipantId: metadata.recall_participant_id,
                },
              ]
              : [];
          } catch {
            return [];
          }
        },
      );

      // Ambiguous (more than one match) is treated the same as no match -
      // stays on the bridge's auto-detect default rather than guessing
      // which stream is really this viewer's.
      setCorrelatedSpeaker(matches.length === 1 ? matches[0] : undefined);
    };

    recompute();
    room.on("participantConnected", recompute);
    room.on("participantDisconnected", recompute);
    room.on("participantMetadataChanged", recompute);
    return () => {
      room.off("participantConnected", recompute);
      room.off("participantDisconnected", recompute);
      room.off("participantMetadataChanged", recompute);
    };
  }, [room, teamsContext?.user]);

  // One dropdown drives both: the viewer's own caption language, and - when
  // correlated to their own speaker stream - that stream's source language
  // too, instead of showing a second selector just for the source side.
  // Sent as a LiveKit data message, not an api-gateway call, since sidepanel
  // viewers are often not signed in - the bridge's own per-participant
  // connection for this speaker (meeting-bots/teams/src/livekit-publisher.ts)
  // listens for its sid and applies the update to its own metadata.
  useEffect(() => {
    if (!correlatedSpeaker) {
      return;
    }
    localParticipant
      .publishData(
        new TextEncoder().encode(
          JSON.stringify({
            participant_id: correlatedSpeaker.sid,
            language: user.metadata.selectedLanguage,
          }),
        ),
        { reliable: true, topic: "update_speaker_language" },
      )
      .catch((error) =>
        console.error("Failed to update speaker language", error),
      );
  }, [correlatedSpeaker, user.metadata.selectedLanguage, localParticipant]);

  // Same correlation, for the AI dubbing voice avatar: the translator reads
  // voice avatar settings from the Recall-published speaker stream's
  // metadata, not the viewer's own sidepanel connection, so a correlated
  // viewer's Settings dialog / Voice picker choice must be pushed there
  // explicitly.
  useEffect(() => {
    if (!correlatedSpeaker) {
      return;
    }
    localParticipant
      .publishData(
        new TextEncoder().encode(
          JSON.stringify({
            participant_id: correlatedSpeaker.sid,
            ai_voice_gender: user.metadata.aiVoiceGender,
            ai_voice_short_name: user.metadata.aiVoice,
          }),
        ),
        { reliable: true, topic: "update_speaker_voice_avatar" },
      )
      .catch((error) =>
        console.error("Failed to update speaker voice avatar", error),
      );
  }, [
    correlatedSpeaker,
    user.metadata.aiVoiceGender,
    user.metadata.aiVoice,
    localParticipant,
  ]);

  // Keyed to the correlated speaker's sid rather than the viewer's own
  // local participant, because the viewer never publishes their own mic in
  // this flow - the bridge does, as a separate LiveKit participant for the
  // correlated Teams attendee. useUserMetadata's own-mic autodetect
  // tracking (hooks/user.ts) checks localParticipant.sid, so it can't pick
  // up this event on its own.
  useEffect(() => {
    if (!correlatedSpeaker) {
      return;
    }
    const decoder = new TextDecoder();
    const onDataReceived = (
      payload: Uint8Array,
      _participant?: unknown,
      _kind?: unknown,
      topic?: string,
    ) => {
      if (topic !== "autodetect_source_language") {
        return;
      }
      const event = JSON.parse(decoder.decode(payload)) as {
        participant_id: string;
        language: string;
      };
      if (event.participant_id !== correlatedSpeaker.sid) {
        return;
      }
      setUser((prevState) => ({
        ...prevState,
        metadata: { ...prevState.metadata, detectedLanguage: event.language },
      }));
    };
    room.on("dataReceived", onDataReceived);
    return () => {
      room.off("dataReceived", onDataReceived);
    };
  }, [room, correlatedSpeaker, setUser]);
  const openSettingsDialog = useCallback(() => {
    teams.dialog.url.open({
      url: `${window.location.origin}/#/teams/settings/${props.room.id}`,
      size: {
        width: 600,
        height: 400,
      },
    });
  }, [props.room]);

  return (
    <>
      <UpgradeNow
        organization={props.organization}
        className={classNames.HeaderUpgradeNow}
        goToCalendly={true}
      />
      {!isUpgradeNowVisible && meetingType === "adhoc" && (
        <h3 className={classNames.HeaderUpgradeNow} style={{ textAlign: "center" }}>
          To stop real-time translation, please close the tab.
        </h3>
      )}
      <div className={classNames.Translator}>
        <canvas
          style={{ display: "none" }}
          ref={canvas}
          width={pipWidth}
          height={pipHeight}
        />
        <RoomAudioRenderer />

        <div className={classNames.Captions}>
          <Captions renderLanguage={renderLanguage} />
        </div>

        <div className={classNames.Toolbar}>
          <UserLanguageSelect
            languages={[
              ...(props.organization.autodetect_enabled ? ["autodetect"] : []),
              ...props.room.languages,
            ]}
          />
          {props.organization.voice_translation_enabled && (
            <Voice hideText={false} text="Voice" teams={true} />
          )}
          {/* Hide overlay for teams mobile */}
          {!isInTeamsMobile && (
            <Button
              color={overlayActive ? "primary" : "gray"}
              backgroundColor="black"
              borderColor={"transparent"}
              onClick={toggleOverlay}
            >
              <MdOutlineClosedCaption />
              Overlay
            </Button>
          )}
          <Button
            color="white"
            backgroundColor="black"
            borderColor="transparent"
            onClick={openSettingsDialog}
          >
            <IoEllipsisHorizontal />
          </Button>
        </div>
      </div>
    </>
  );
};

function ProExperience(props: { room: Room; organization: Organization }) {
  const [invitingBot, setInvitingBot] = useState(false);
  const inMeeting = useInMeeting();

  const axios = useAxios();
  const { token } = usePublisherToken(axios);

  const [botStatus, setBotStatus] = useState<BotStatus | undefined>();
  // Waiting room counts as "active" too - show the translator screen (with
  // its own loading affordances) as soon as the bot is on its way in,
  // instead of blocking behind a dedicated wait screen until it's fully
  // in the call.
  const botActive = botStatus === "waiting_room" || botStatus === "in_call";
  const getBotStatus = api.useGetBotStatus(axios);
  const inviteBot = api.useInviteBot(axios);

  useEffect(() => {
    const getBotStatusRequest = async () => {
      const response = await getBotStatus({
        roomId: props.room.id,
      });
      setBotStatus(response.data.status);
    };

    const getBotStatusRequestInterval = setInterval(() => {
      if (!botActive) {
        getBotStatusRequest();
      }
    }, 2 * 1000);

    getBotStatusRequest();

    return () => {
      clearInterval(getBotStatusRequestInterval);
    };
  }, [getBotStatus, props.room.id, botActive]);

  const [meetingUrl, setMeetingUrl] = useState(props.room.meeting_url || "");
  const updateRoom = api.useUpdateRoom(axios);

  // Persist the pasted link as the room's meeting_url so it survives
  // navigating away and back before "Invite AI translator" is clicked
  useEffect(() => {
    if (meetingUrl === (props.room.meeting_url || "")) {
      return;
    }
    const timeout = setTimeout(() => {
      updateRoom({ id: props.room.id, meeting_url: meetingUrl }).catch(
        (error) => console.error("Failed to save meeting link", error)
      );
    }, 800);
    return () => clearTimeout(timeout);
  }, [meetingUrl, props.room.id, props.room.meeting_url, updateRoom]);

  const parseJoinInfo = useCallback((joinInfo: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(joinInfo, "text/html");

    // Find the link with a specific id
    let joinLink = doc.getElementById("meet_invite_block.action.join_link") as
      | HTMLAnchorElement
      | null
      | undefined;

    if (!joinLink) {
      const links = Array.from(doc.getElementsByTagName("a"));
      joinLink = links.find((link) => link.href.includes("meetup-join"));
    }

    if (!joinLink) {
      throw new Error("Failed to parse Teams join info!");
    }

    return joinLink.href;
  }, []);

  const openInviteBotHelpDialog = useCallback(() => {
    teams.dialog.url.open({
      url: `${window.location.origin}/#/teams/invite-bot-help`,
      size: {
        width: 600,
        height: 400,
      },
    });
  }, []);

  const [user] = useUser();

  if (botStatus === undefined) {
    return <Loader />;
  }

  if (invitingBot) {
    return (
      <div className={classNames.SidePanel}>
        <div className={classNames.InviteBot}>
          <Loader inline={true} style={{ marginTop: "24px" }} />
          <h1>Your AI translator is joining the meeting</h1>
          <h2>
            This might take a few seconds. The real-time translation will
            appear on this tab when you join.
          </h2>
        </div>
      </div>
    );
  }

  if (!inMeeting) {
    if (!user.auth) {
      return (
        <>
          <Header hideUserNav={true} disableLogoClick={true} />
          <div className={classNames.UnauthNotInMeeting}>
            <h2>Please join the call to experience real-time translation</h2>
            <div className={classNames.Languages}>
              <label>Languages</label>
              {props.room.languages.map((language) => (
                <div key={language}>
                  {getLanguageFlag(language)} {getLanguageName(language)}
                </div>
              ))}
            </div>
          </div>
        </>
      );
    }
    return (
      <>
        <OrganizationProvider>
          <Header hideUserNav={true} disableLogoClick={true} />
          <CreateOrEditRoom />
        </OrganizationProvider>
      </>
    );
  }

  // Bot has been invited but hasn't reached the meeting yet (still
  // connecting, before it's even in the waiting room)
  if (botStatus === "joining") {
    return (
      <div className={classNames.SidePanel}>
        <div className={classNames.InviteBot}>
          <Loader inline={true} style={{ marginTop: "24px" }} />
          <h1>Your AI translator is joining the meeting</h1>
          <h2>
            This might take a few seconds. The real-time translation will
            appear on this tab once it joins.
          </h2>
        </div>
      </div>
    );
  }

  // Invite meeting bot
  if (!botActive) {
    return (
      <>
        <div className={classNames.SidePanel}>
          <div className={classNames.InviteBot}>
            <h1>Invite your AI translator to the meeting</h1>
            <div className={classNames.InviteBotOptions}>
              <h2>Copy and paste the join info link below</h2>
              <input
                type="url"
                placeholder="Enter your meeting URL here"
                value={meetingUrl}
                onPaste={(e) => {
                  // If html, parse join info
                  if (e.clipboardData.types.includes("text/html")) {
                    // Prevent onChange if handling paste
                    e.preventDefault();
                    const input = e.clipboardData.getData("text/html");
                    const url = parseJoinInfo(input);
                    if (url) {
                      setMeetingUrl(url);
                    }
                  }
                }}
                onChange={(e) => {
                  const input = e.target.value;
                  // If html, parse join info
                  if (input.includes("<")) {
                    const url = parseJoinInfo(input);
                    if (url) {
                      setMeetingUrl(url);
                    }
                  } else {
                    setMeetingUrl(input);
                  }
                }}
              />
              <div
                className={classNames.InviteBotHelp}
                onClick={() => openInviteBotHelpDialog()}
              >
                Where can I find this?
              </div>
            </div>
            <div className={classNames.InviteBotOptions}>
              <h2 style={{ fontWeight: 600 }}>Languages</h2>
              <div className={classNames.PreCallConfigLanguages}>
                {props.room.languages
                  .sort((a, b) =>
                    a === "autodetect" ? 1 : b === "autodetect" ? -1 : 0,
                  )
                  .map((language) => (
                    <div key={language}>{`${getLanguageFlag(
                      language,
                    )} ${getLanguageName(language)}`}</div>
                  ))}
              </div>
            </div>
            <Button
              disabled={
                !meetingUrl.length ||
                props.organization.translation_seconds_remaining <= 0
              }
              onClick={
                meetingUrl.length
                  ? async () => {
                    try {
                      setInvitingBot(true);
                      await inviteBot({
                        roomId: props.room.id,
                        meetingUrl: meetingUrl,
                      });
                      setBotStatus("joining");
                    } finally {
                      setTimeout(() => {
                        setInvitingBot(false);
                      }, 2 * 1000);
                    }
                  }
                  : undefined
              }
            >
              Invite AI translator
            </Button>
            <UpgradeNow organization={props.organization} goToCalendly={true} />
          </div>
        </div>
      </>
    );
  }

  return (
    <div className={classNames.SidePanel}>
      <LiveKitRoom
        audio={false}
        video={false}
        connect={true}
        token={token}
        connectOptions={{ autoSubscribe: false }}
        serverUrl={import.meta.env.VITE_LIVEKIT_URL}
      >
        <Translator room={props.room} organization={props.organization} />
      </LiveKitRoom>
    </div>
  );
}

function SidePanel() {
  const { room, organization, loading } = useRoom();

  if (!room || !organization || loading) {
    return <Loader />;
  }

  return <ProExperience room={room} organization={organization} />;
}

export default SidePanel;
